/**
 * SceneDots — the persistent side-dot navigator used by Story (and
 * Pitch) to indicate scroll position and let the visitor jump to any
 * scene.
 *
 * Built for Phase 4 of the pilot React-engine port (see
 * `docs/eng/pilot-react-engine.md`). Audit finding #10 — every active
 * scene dot must carry `aria-current="step"` — is killed structurally
 * here: the active dot bakes in `aria-current="step"` and inactive
 * dots explicitly carry no aria-current attribute. There is no path
 * in this component that produces a dot without correct aria.
 *
 * F17 of the Oatmeal rebuild: dot styling is fully inline-Tailwind
 * (the prior `.story-dot` / `.story-dot:hover` / `.story-dot.is-active`
 * rules in pilot-engine.css are gone). Pitch.tsx renders its dots
 * with the same utility classes so the visual stays consistent.
 */

import React from 'react';
import {clsx} from 'clsx/lite';

/**
 * Tailwind utilities for a single scene dot. Exported so Pitch.tsx
 * can reuse the exact same look without duplicating string literals.
 */
export const STORY_DOT_CLASS =
  'size-2.5 cursor-pointer rounded-full border-[1.5px] border-taupe-300 bg-transparent transition-[transform,background-color,border-color] duration-200 hover:scale-[1.4] dark:border-taupe-700';

/** Active-state extension class. */
export const STORY_DOT_ACTIVE_CLASS =
  'bg-taupe-950 border-taupe-950 dark:bg-taupe-100 dark:border-taupe-100';

export interface SceneDescriptor {
  /** id of the scrolled-into element. Matches the section's DOM id. */
  id: string;
  /** Human-readable label for the screen-reader and tooltip. */
  title: string;
}

export interface SceneDotsProps {
  scenes: SceneDescriptor[];
  /** id of the currently-active scene, or `null` if none yet. */
  activeId: string | null;
  /** Called when a dot is clicked. Receives the dot's scene id. */
  onSelect: (id: string) => void;
  /** Additional CSS class on the root `<nav>`. */
  className?: string;
}

export default function SceneDots({
  scenes,
  activeId,
  onSelect,
  className,
}: SceneDotsProps) {
  return (
    <nav
      className={clsx(
        // `story-dots` (class) is the hook the pilot-engine.css
        // `body:not(.story-mode) .story-dots { display: none }` rule
        // selects on. Without the class, the navigator stays visible
        // in demo / pitch / vision / sandbox modes and ghost-renders
        // as a column of empty circles on the left edge.
        //
        // Hidden under sm (640px). On phones the dot column overlaps
        // content + the bottom CTAs already give a "continue" path;
        // the May 2026 mobile review flagged the dots as unusable at
        // that width.
        'story-dots fixed left-6 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-3 sm:flex',
        className,
      )}
      id="story-dots"
      aria-label="Story scenes"
    >
      {scenes.map((scene) => {
        const isActive = scene.id === activeId;
        return (
          <button
            key={scene.id}
            type="button"
            className={clsx(STORY_DOT_CLASS, isActive && STORY_DOT_ACTIVE_CLASS)}
            data-target={scene.id}
            data-label={scene.title}
            aria-label={scene.title}
            aria-current={isActive ? 'step' : undefined}
            onClick={() => onSelect(scene.id)}
          />
        );
      })}
    </nav>
  );
}
